// auth-web/src/features/auth/consentPolicy.ts
//
// app_id 별 약관 동의 항목 정책.
//
// book_tox(apps/web) 에 같은 파일이 있다. 두 프론트가 각각 진입점이라 사본이 두 벌인데,
// 항목 구성이 어긋나면 서비스마다 다른 약관을 받게 된다. 한쪽을 고치면 반대쪽도 볼 것.
//
// auth-web 에는 /legal/* 라우트가 없다. 그래서 전문보기는 내부 이동이 아니라
// 외부 URL(book_tox 의 legal 페이지)로 연다. legalUrl 이 없으면 버튼 자체를 안 그린다.

export type ConsentItem = {
    /** user_consents.code 로 저장될 값 */
    code: string;
    label: string;
    required: boolean;
    /** 필수 항목은 버전이 있어야 서버 /register 검증을 통과한다 */
    version?: string;
    /** 약관 전문 외부 URL. 없으면 "전문보기" 버튼을 그리지 않는다 */
    legalUrl?: string;
    /** 항목 우측 회색 보조문구 (legalUrl 이 없을 때만 표시) */
    note?: string;
};

export type ConsentPolicy = {
    app_id: string;
    name: string;
    items: ConsentItem[];
};

const TERMS_VERSION   = import.meta.env.VITE_TERMS_VERSION   ?? "2025-09-01";
const PRIVACY_VERSION = import.meta.env.VITE_PRIVACY_VERSION ?? "2025-09-01";

const PAGEBOX_TERMS_VERSION   = import.meta.env.VITE_PAGEBOX_TERMS_VERSION   ?? "1.0";
const PAGEBOX_PRIVACY_VERSION = import.meta.env.VITE_PAGEBOX_PRIVACY_VERSION ?? "1.0";

/** 약관 전문이 실제로 존재하는 곳. auth-web 에는 사본을 두지 않는다(개정 시 어긋남). */
const LEGAL_BASE = import.meta.env.VITE_LEGAL_BASE ?? "https://innobooktox.com/#/legal";

export const DEFAULT_APP_ID = "booktalk";

/** app_id 를 세션에 보관하는 키. 전문보기를 다녀오면 쿼리가 날아가서 필요하다. */
export const APP_ID_SS_KEY = "bt.app_id";

/**
 * 동의 체크 상태를 세션에 보관하는 키.
 *
 * 가입 화면에서 뒤로 돌아오면 체크를 그대로 복원해야 해서 동의 → 가입 이동 시점에는
 * 지우지 않는다. 실제 삭제는 가입 성공 직후 한 번만 (RegisterPage 의 bt.agreements 정리와 같은 자리).
 * 값에는 app_id 가 함께 들어가므로 다른 서비스의 체크가 복원되지는 않는다.
 */
export const CHECKS_SS_KEY = "bt.agree.checks";

const POLICIES: Record<string, ConsentPolicy> = {
    // PageBox loads admin-managed documents and their consent versions on the agreement screen.
    pagebox: {
        app_id: "pagebox",
        name: "PageBox",
        items: [
            { code: "terms",   label: "서비스 이용약관 동의",   required: true, version: PAGEBOX_TERMS_VERSION },
            { code: "privacy", label: "개인정보처리방침 동의", required: true, version: PAGEBOX_PRIVACY_VERSION },
        ],
    },

    booktalk: {
        app_id: "booktalk",
        name: "독서노트",
        items: [
            { code: "terms",   label: "서비스 이용약관 동의",   required: true, version: TERMS_VERSION,   legalUrl: `${LEGAL_BASE}/terms` },
            { code: "privacy", label: "개인정보처리방침 동의", required: true, version: PRIVACY_VERSION, legalUrl: `${LEGAL_BASE}/privacy` },
        ],
    },
};

export type ResolvedPolicy = {
    policy: ConsentPolicy;
    fellBack: boolean;
    requestedAppId: string;
};

/**
 * app_id → 정책. 모르는 app_id 는 기본 정책(필수 전체 노출)으로 떨어뜨린다.
 * 항목을 덜 보여주는 쪽으로 실패하면 안 되므로 "가장 많이 받는 쪽" 이 안전한 기본값이다.
 */
export function resolveConsentPolicy(appId: string | null | undefined): ResolvedPolicy {
    const requested = String(appId || "").trim();
    const hit = requested ? POLICIES[requested] : undefined;

    if (hit) return { policy: hit, fellBack: false, requestedAppId: requested };

    return {
        policy: POLICIES[DEFAULT_APP_ID],
        fellBack: !!requested,
        requestedAppId: requested || DEFAULT_APP_ID,
    };
}

/**
 * 체크 상태 → /register 로 보낼 agreements.
 *
 * terms_version/privacy_version 은 서버가 필수로 보는 기존 계약이라 그대로 둔다.
 * app_id 를 빠뜨리면 서버가 booktalk 으로 기록하므로 반드시 실어야 한다.
 */
export function buildAgreements(policy: ConsentPolicy, checked: Record<string, boolean>) {
    const find = (code: string) => policy.items.find((i) => i.code === code);

    return {
        terms_version:   find("terms")?.version   ?? "",
        privacy_version: find("privacy")?.version ?? "",
        marketing_sms:   !!checked["marketing_sms"],

        app_id: policy.app_id,
        consents: policy.items.map((i) => ({
            code: i.code,
            version: i.version ?? null,
            required: i.required,
            agreed: !!checked[i.code],
        })),
    };
}